// THE FOCUSED MOVE PAGE — Pay approved address, Move to Treasury, Refill Hot (UI rebuild batch 4, custody hand-off).
// Astra's form anatomy (02-screen-review/pay, custody-move, refill): one card — the destination fixed and named, the
// source by class, the amount, "More details" folded — and the footer's one gold primary, "Review …", which turns into
// the readback and the confirm. From lg the figure the move draws on sits beside the card.
//
// The state machine is useMoveAttempt (MoveSheet.tsx's, copied). Every line it can draw is the sheet's, in the same
// order and on the same conditions: the test-only amount, reading / failed reads, the Flagged one-release lines, the
// caps' allowance, the server's refusal (moveRefusal, with ITS source), sending, uncertain (Send the same again /
// Change the payment), the by-key question, the window. Only the layout and the review step are new.
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { PayoutSource } from '../../../lib/api';
import { CLASS_WORD, groupDigits, money } from '../../../lib/custody-format';
import { reviewStands, type Reviewed } from '../../../lib/custody-screen';
import { moveRefusal, moveRefusalMore } from '../../../screens/custody/refusals';
import { HeldForField } from '../../HeldForField';
import { StateLine, Pending, Fact } from '../kit';
import { MoreDetails } from '../../MoreDetails';
import { FormFrame, FormFoot, PRIMARY, QUIET } from '../move/FormFrame';
import { WindowLine } from './parts';
import type { MoveAttemptView } from './useMoveAttempt';
import { DealField } from './DealField';

const errorText = (err: unknown): string => String((err as { message?: unknown } | null)?.message ?? err);

export function MoveForm({ title, review, verb, v, sources, lead, facts, aside, about, refusal, notice, dealParty }: {
  title: string;
  /** The primary's first face: "Review payout", "Review transfer", "Review refill". */
  review: string;
  /** The confirm's word: "Pay", "Move", "Refill". */
  verb: string;
  v: MoveAttemptView;
  sources: readonly PayoutSource[];
  /** One context line at the top of the card (Pay: the destination's stage). */
  lead?: ReactNode;
  /** The fixed facts: payee, destination (gated), To. */
  facts: ReadonlyArray<{ label: string; value: ReactNode }>;
  aside?: ReactNode;
  about: string;
  /** Set when nothing can be pressed at all (no payable entry, no Treasury wallet): the one line, and no button. */
  refusal?: ReactNode;
  /** A read the form stands on failed to refresh: said above everything, on every width. */
  notice?: ReactNode;
  /** A payout's payee (P-063): its open trades are offered as the deal the payout settles, optional. */
  dealParty?: string;
}) {
  // The review holds for the depositor it was made with (Screens on cfc0d1f8): a different one — chosen, or the
  // list changed under it — is a payment not yet reviewed.
  const [reviewed, setReviewed] = useState<Reviewed>(false);
  const reviewing = reviewStands(reviewed, v.heldFor);
  const setReviewing = (on: boolean) => setReviewed(on ? { heldFor: v.heldFor } : false);
  const amountText = v.shownAmount.trim() && !v.problem ? `${groupDigits(v.shownAmount.trim())} USDT` : '';
  const fromWord = CLASS_WORD[v.shownFrom] ?? v.shownFrom;
  const toApprovals = <Link to="/custody" className={PRIMARY}>Open Approvals</Link>;

  // ── recorded: what the desk holds now, and the way to it ──────────────────────────────────────────────────────
  if (v.queued) {
    const outcome = v.attempt?.outcome;
    return (
      <FormFrame title={title} aside={aside}>
        {facts.map((f) => <Fact key={f.label} label={f.label}>{f.value}</Fact>)}
        {outcome === 'sending' ? (
          <>
            <StateLine tone="warn" more="The desk has broadcast this payment and waits for the chain to confirm it; if the chain refuses it, the desk queues it again for your signature.">
              Sent — confirming on the chain.
            </StateLine>
            {v.byKey.isFetching && <Pending inline what="Asking the desk…" />}
            {v.byKey.isError && !v.byKey.isFetching && (
              <StateLine tone="bad" more={errorText(v.byKey.error)} action={<button type="button" className="btn-link tap-44 text-body" onClick={() => void v.byKey.refetch()}>Try again</button>}>
                Couldn&apos;t read what became of it.
              </StateLine>
            )}
          </>
        ) : outcome === 'sent' ? (
          <StateLine tone="good" more="The desk had recorded this press and has sent it already: there is nothing to sign and nothing to press again.">
            Already sent — nothing to sign.
          </StateLine>
        ) : (
          <StateLine tone="good" more="The desk builds the transaction and offers it in Approvals for your Ledger. Nothing has moved yet.">
            Queued — sign it in Approvals.
          </StateLine>
        )}
        <FormFoot note={`${amountText} · from ${fromWord}`}>{toApprovals}</FormFoot>
      </FormFrame>
    );
  }

  const confirming = reviewing && !v.blocked && !v.locked;
  return (
    <FormFrame title={title} aside={aside}>
      {notice}
      {lead && <p className="rounded-lg border border-stroke bg-panel-2 px-4 py-3 text-meta text-tg-hint">{lead}</p>}
      <div>
        {facts.map((f) => <Fact key={f.label} label={f.label}>{f.value}</Fact>)}
      </div>

      {refusal ? (
        <>
          {refusal}
          <MoreDetails label="About this job"><p className="text-meta text-tg-hint">{about}</p></MoreDetails>
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {sources.length > 1 ? (
              <label className="block">
                <span className="mb-1 block text-body font-medium">Source</span>
                <select className="select w-full" value={v.shownFrom} disabled={v.locked} onChange={(e) => { setReviewing(false); v.setFrom(e.target.value as PayoutSource); }}>
                  {sources.map((s) => <option key={s} value={s}>{CLASS_WORD[s] ?? s} · USDT</option>)}
                </select>
              </label>
            ) : (
              <div>
                <span className="mb-1 block text-body font-medium">From</span>
                <p className="flex min-h-[44px] items-center text-body lg:min-h-[40px]">{fromWord}</p>
              </div>
            )}
            <label className="block">
              <span className="mb-1 block text-body font-medium">Amount, USDT</span>
              <input
                className="input w-full font-mono"
                inputMode="decimal"
                placeholder="0.00"
                value={v.shownAmount}
                autoComplete="off"
                readOnly={v.locked || v.fixedAmount !== null}
                onChange={(e) => { setReviewing(false); v.setAmount(e.target.value); }}
              />
            </label>
          </div>
          {/* words:ok — amountProblem's own sentence (lib/custody-format.ts) */}
          {v.problem && <StateLine tone="bad">{v.problem}</StateLine>}
          {!v.locked && v.fixedAmount !== null && (
            <StateLine more="Whitelisting includes a test: the first payment to a new address is exactly this, and nothing else goes until the customer confirms it and you press Received on Payout addresses.">
              First payment here is the {v.fixedAmount} USDT test.
            </StateLine>
          )}
          <Fact label="Network">TRC20</Fact>

          {/* What a new payment is judged by: read, or said to be waiting or failed — a locked attempt needs none of it. */}
          {!v.locked && v.readiness.state === 'waiting' && <Pending inline what={`Reading ${v.readiness.what}…`} />}
          {!v.locked && v.readiness.state === 'failed' && (
            <StateLine tone="bad" more={errorText(v.readError)} action={<button type="button" className="btn-link tap-44 text-body" disabled={v.readsPending} onClick={v.retryReads}>Try again</button>}>
              Couldn&apos;t read {v.readiness.what}.
            </StateLine>
          )}
          {dealParty && v.withDeal && <DealField party={dealParty} value={v.deal} locked={v.locked} onChange={(x) => { setReviewing(false); v.setDeal(x); }} />}
          {v.shownFrom === 'frozen' && v.deskReturn && (
            <div className="flex items-baseline justify-between gap-3 border-b border-stroke-soft py-3">
              <span className="text-meta text-tg-hint">The desk&apos;s money in Flagged</span>
              <span className="text-right text-body">{v.deskHeld ? <span className="font-mono tabular">{money(v.deskHeld.held, 'USDTTRC')}</span> : v.deskHeldFailed ? <span className="text-neg">Not read</span> : v.deskHeldRead ? <span className="text-tg-hint">Not known yet</span> : 'Reading…'}</span>
            </div>
          )}
          {v.shownFrom === 'frozen' && !v.deskReturn && (
            <HeldForField holders={v.holders} failed={v.holdersFailed} retry={v.retryHolders} chosen={v.heldChoice} onChoose={v.setHeldChoice}
              locked={v.locked && v.attempt ? (v.attempt.heldFor ?? null) : undefined} />
          )}
          {!v.locked && v.shownFrom === 'frozen' && v.releaseOpen === false && (
            <StateLine tone="warn" more="One release from Flagged at a time (the operator, 22 Oct 2026): a second is refused until the first is signed or completes.">
              Flagged releases one at a time.
            </StateLine>
          )}
          {!v.locked && v.releaseOpen === true && (
            <StateLine tone="warn" more="An open release from Flagged is already waiting for your Ledger. Sign it in Approvals, or let it expire, before queuing another."
              action={<Link to="/custody" className="btn-link text-body">Approvals</Link>}>
              A release is already waiting — sign it first.
            </StateLine>
          )}
          {/* words:ok — the caps' own line carries its figures (lib/custody-screen.ts moveAllowance) */}
          {!v.locked && !v.allowance.ok && <StateLine tone="warn" more="What the caps allow for this source today, counted before the press; the server checks the same.">{v.allowance.line}</StateLine>}
          {/* words:ok — the server's own line carries the figures; the refusal keeps the source it was for */}
          {v.refused && <StateLine tone="bad" more={moveRefusalMore(v.refused.error)}>{moveRefusal(v.refused.error, v.refused.from)}</StateLine>}
          {v.pending && (
            <StateLine tone="warn" more="The press is on its way to the desk. Its answer settles it; nothing else is pressed meanwhile.">
              Sending — waiting for the desk&apos;s answer.
            </StateLine>
          )}
          {v.uncertain && v.attempt && (
            <StateLine tone="warn" more={`${v.attempt.error ? `${errorText(v.attempt.error)}. ` : ''}The desk may have recorded this payment. This page asks the desk what became of this press every few seconds. Pressing again sends the same instructions under the same key: the desk answers with the row it made, or makes it once — never twice. Change the payment asks the desk to close this press first.`}>
              Not confirmed — the payment may be recorded.
            </StateLine>
          )}
          {v.locked && v.byKey.isFetching && !v.voiding.isPending && <Pending inline what="Asking the desk what became of it…" />}
          {v.locked && v.byKey.isError && !v.byKey.isFetching && (
            <StateLine tone="bad" more={errorText(v.byKey.error)} action={<button type="button" className="btn-link tap-44 text-body" onClick={() => void v.byKey.refetch()}>Try again</button>}>
              Couldn&apos;t read what became of the press.
            </StateLine>
          )}
          {v.uncertain && v.voiding.isError && <StateLine tone="bad" more={errorText(v.voiding.error)}>Couldn&apos;t close this press — try again.</StateLine>}

          {/* The window gates every move: one line, its opener beside it. */}
          {!v.locked && <WindowLine />}

          <div className="border-t border-stroke-soft pt-3">
            <MoreDetails label="More"><p className="text-meta text-tg-hint">{about}</p></MoreDetails>
          </div>

          {/* THE READBACK — what will be queued, each fact once. */}
          {confirming && (
            <p className="text-body text-tg-text">{verb} {amountText} · {facts[0]?.value} · from {fromWord}{v.heldFor !== null ? ` · held for ${v.holders?.find((h) => h.partySlug === v.heldFor)?.partyName ?? v.heldFor}` : ''}{v.deal ? ` · for ${v.deal}` : ''}</p>
          )}

          <FormFoot note={confirming ? 'Nothing moves until you sign it in Approvals.' : 'Review once before confirming.'}>
            {v.uncertain ? (
              <>
                <button type="button" className={QUIET} disabled={v.mutating || v.voiding.isPending} onClick={v.voidPress}>
                  {v.voiding.isPending ? 'Closing this press…' : 'Change the payment'}
                </button>
                <button type="button" className={PRIMARY} disabled={v.mutating || v.voiding.isPending} onClick={v.press}>
                  {v.mutating ? 'Sending…' : 'Send the same again'}
                </button>
              </>
            ) : confirming ? (
              <>
                <button type="button" className={QUIET} disabled={v.mutating} onClick={() => setReviewing(false)}>Edit</button>
                <button type="button" className={PRIMARY} disabled={v.blocked || v.mutating} onClick={v.press}>
                  {v.pending || v.mutating ? 'Queuing…' : `${verb} ${amountText}`.trim()}
                </button>
              </>
            ) : (
              <button type="button" className={PRIMARY} disabled={v.blocked || v.mutating || v.pending} onClick={() => setReviewing(true)}>
                {v.pending || v.mutating ? 'Queuing…' : `${review} →`}
              </button>
            )}
          </FormFoot>
        </>
      )}
    </FormFrame>
  );
}
